import { useState } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { relationshipConfig } from './relationshipConfig';

import { logger } from '@/utils/logger';
import { Button } from '@/components/ui/button';
import { ArrowRight, Calendar, Check, ChevronDown, ClipboardList, Copy, Edit3, Gift, Package, Search, Share2, Sparkles, Trash2, Users, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import InlineGiftAdder from '@/components/keepsakes/InlineGiftAdder';
import ProductCard from '@/components/ProductCard';
// Keepsake Box Component - Simplified and streamlined
export default function MemoryBox({ 
  recipient, 
  savedGifts, 
  questionnaires, 
  isOpen, 
  onToggle, 
  onDelete, 
  onAddGift, 
  onRemoveGift, 
  onCreateQuestionnaire, 
  onDeleteQuestionnaire, 
  onAnalyzeQuestionnaire,
  analyzingId,
  onManageOccasions, 
  onEditRecipient, 
  onFindGifts, 
  onInlineGiftAdded,
  user,
  darkMode 
}) {
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);

  const config = relationshipConfig[recipient.relationship] || relationshipConfig['Friend'];
  const cardColor = recipient.color || config.color;
  const RelIcon = config.icon;
  const giftCount = savedGifts.length;
  
  // Find questionnaire for this recipient
  const questionnaire = questionnaires.find(q => 
    q.recipient_name.toLowerCase() === recipient.name.toLowerCase()
  );

  // Check setup completion for onboarding
  const hasOccasions = recipient.occasions && recipient.occasions.length > 0;
  const hasPreferences = recipient.gift_preferences && Object.keys(recipient.gift_preferences).length > 0;
  const hasQuestionnaire = !!questionnaire;
  // Check if we have ANY profile data for AI recommendations (interests, preferences, or notes)
  const hasAnyProfileData = hasPreferences || 
    (recipient.interests && recipient.interests.length > 0) || 
    (recipient.notes && recipient.notes.trim().length > 0);

  const bgPattern = config.bgPattern === 'hearts' 
    ? `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 17.5l-1.5-1.3C4.5 12.5 2 10.3 2 7.5 2 5.4 3.7 3.8 5.8 3.8c1.2 0 2.3.6 3 1.5.7-.9 1.8-1.5 3-1.5 2.1 0 3.8 1.6 3.8 3.7 0 2.8-2.5 5-6.5 8.7L10 17.5z' fill='%23ffffff'/%3E%3C/svg%3E")`
    : config.bgPattern === 'stars'
    ? `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 1l2.5 6.5H19l-5.3 4 2 6.5L10 14l-5.7 4 2-6.5L1 7.5h6.5L10 1z' fill='%23ffffff'/%3E%3C/svg%3E")`
    : `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='3' fill='%23ffffff'/%3E%3C/svg%3E")`;

  // Consolidated Find Gifts handler - checks preferences and routes appropriately
  const handleFindGifts = (e) => {
    e.stopPropagation();
    if (hasAnyProfileData) {
      // Has profile data (preferences, interests, or notes) - use AI recommendations
      onFindGifts(recipient);
    } else {
      // No profile data - go to browse all
      onAddGift();
    }
  };

  const handleShare = async (e) => {
    if (e) e.stopPropagation();
    
    try {
      // First, get the share link
      const response = await api.post(`/recipients/${recipient.id}/share`, {
        permission: 'view'
      });
      const fullLink = `${window.location.origin}${response.data.share_url}`;
      
      // Debug: Log what's available
      
      // Check if Web Share API is available (mobile devices)
      if (navigator.share) {
        try {
          await navigator.share({
            title: `${recipient.name}'s Gift Profile`,
            text: `Check out ${recipient.name}'s gift profile with saved gift ideas, interests, and occasions!`,
            url: fullLink
          });
          toast.success('Shared successfully!');
          return; // Exit after successful share
        } catch (shareError) {
          // User cancelled the share
          if (shareError.name === 'AbortError') {
            return; // Exit silently
          }
          // Other error - fall through to modal
        }
      } else {
      }
      
      // Fallback: Show copy link modal (desktop or if share failed)
      setShareLink(fullLink);
      setShowShareModal(true);
      
    } catch (error) {
      logger.error('Failed to generate share link:', error);
      toast.error('Failed to generate share link');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied to clipboard!');
  };

  return (
    <div className="relative">
      {!isOpen ? (
        // CLOSED STATE
        <div className="animate-in fade-in zoom-in-95 duration-300">
          <div 
            className="relative p-6 rounded-2xl overflow-hidden cursor-pointer transition-all hover:shadow-xl hover:scale-[1.02]"
            onClick={onToggle}
            style={{
              background: `linear-gradient(135deg, ${cardColor} 0%, ${cardColor}dd 100%)`,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              minHeight: '160px'
            }}
          >
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: bgPattern }} />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-full" style={{ background: 'rgba(255,255,255,0.3)' }} />
            <div className="absolute top-1/2 left-0 w-full h-6 -translate-y-1/2" style={{ background: 'rgba(255,255,255,0.3)' }} />
            <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-1">
              <div className="w-6 h-4 rounded-full" style={{ background: 'rgba(255,255,255,0.5)' }} />
              <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(255,255,255,0.6)' }} />
              <div className="w-6 h-4 rounded-full" style={{ background: 'rgba(255,255,255,0.5)' }} />
            </div>
            <div className="relative z-10 text-center mt-6">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <RelIcon className="w-6 h-6" style={{ color: cardColor }} />
              </div>
              <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Playfair Display, serif', color: 'white' }}>{recipient.name}</h3>
              <p className="text-sm opacity-90" style={{ color: 'white' }}>{recipient.relationship}</p>
            </div>
            {giftCount > 0 && (
              <div className="absolute bottom-3 right-3 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: 'rgba(255,255,255,0.9)', color: cardColor }}>
                {giftCount} {giftCount === 1 ? 'gift' : 'gifts'}
              </div>
            )}
            {/* One-click instant recommendations from this person's profile */}
            <button
              onClick={handleFindGifts}
              className="absolute top-3 right-3 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all hover:scale-105"
              style={{ background: 'rgba(255,255,255,0.92)', color: cardColor }}
              title={hasAnyProfileData ? `Instant gift ideas for ${recipient.name}` : `Browse gifts for ${recipient.name}`}
              data-testid={`quick-gift-ideas-${recipient.id}`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              Gift ideas
            </button>
            <div className="absolute bottom-3 left-3">
              <ChevronDown className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.8)' }} />
            </div>
          </div>
        </div>
      ) : (
        // OPEN STATE - Simplified
        <div className="animate-in fade-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto">
          {/* No inner rounded clipper, the DialogContent owns the rounded
              clip + overflow-hidden so there's no nested-radius seam where
              the page can blur through at the top corners. */}
          <div style={{ background: cardColor, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            {/* Simplified Header - Just name, relationship, and essential actions.
                rounded-t-2xl matches the modal/card top curve so the cardColor
                bg fills the corners cleanly (avoids any sub-pixel bleed at the
                rounded edges of the overflow-hidden parent). */}
            <div className="p-4 rounded-t-2xl" style={{ background: cardColor }}>
              <div className="flex items-center justify-between">
                <div onClick={onToggle} className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.9)' }}>
                    <RelIcon className="w-5 h-5" style={{ color: cardColor }} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold leading-tight" style={{ fontFamily: 'Playfair Display, serif', color: 'white' }}>{recipient.name}</h3>
                    <p className="text-xs opacity-80" style={{ color: 'white' }}>{recipient.relationship}</p>
                  </div>
                  {/* Occasion chips, sit on the same horizontal baseline as
                      the action buttons (parent flex is items-center). Each
                      chip shows the occasion name + nicely formatted date. */}
                  {hasOccasions && (
                    <div className="hidden sm:flex flex-wrap items-center gap-1.5 ml-1">
                      {recipient.occasions.map((occ, idx) => {
                        let dateLabel = '';
                        if (occ.date) {
                          const parsed = new Date(occ.date);
                          if (!isNaN(parsed.getTime())) {
                            dateLabel = parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                          }
                        }
                        return (
                          <span
                            key={`${occ.name}-${idx}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium leading-none whitespace-nowrap"
                            style={{
                              background: 'rgba(255,255,255,0.22)',
                              color: 'white',
                              backdropFilter: 'blur(2px)'
                            }}
                            title={dateLabel ? `${occ.name} · ${dateLabel}` : occ.name}
                            data-testid={`recipient-occasion-chip-${idx}`}
                          >
                            <Calendar className="w-3.5 h-3.5 opacity-90" />
                            <span>{occ.name}</span>
                            {dateLabel && (
                              <span className="opacity-80 font-normal">· {dateLabel}</span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Minimal Action Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); onEditRecipient(); }}
                    className="p-2 rounded-lg transition-colors hover:bg-white/20 hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                    title="Preferences & profile"
                    data-testid={`view-preferences-btn-${recipient.id}`}
                  >
                    <ClipboardList className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onEditRecipient(); }}
                    className="p-2 rounded-lg transition-colors hover:bg-white/20 hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                    title="Edit profile & preferences"
                  >
                    <Edit3 className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleShare(); }}
                    className="p-2 rounded-lg transition-colors hover:bg-white/20 hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                    title="Share profile"
                  >
                    <Share2 className="w-4 h-4 text-white" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDelete(); }} 
                    className="p-2 rounded-lg transition-colors hover:bg-white/20 hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                    title="Delete keepsake"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onToggle(); }} 
                    className="p-2 rounded-lg transition-colors hover:bg-white/20 hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                    title="Close"
                    data-testid="keepsake-modal-close"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Content Area, rounded-b-2xl matches the wrapper's bottom
                curve so its bg fills cleanly to the corners. In dark mode
                we use a neutral dark surface (not a tint of cardColor) so
                inner sections (decks, interest pills, analysis banner,
                etc.) keep their accent contrast instead of disappearing
                into a single wash. */}
            <div className="p-5 rounded-b-2xl" style={{ background: darkMode?.isDark ? '#1a1b23' : 'white' }}>
              
              {/* Questionnaire Section */}
              {!questionnaire ? (
                <div className="mb-4 p-4 rounded-xl border-2 border-dashed" style={{ borderColor: darkMode?.isDark ? 'rgba(254, 215, 165, 0.3)' : '#E5E5E5' }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium mb-1 flex items-center gap-2" style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>
                        <Sparkles className="w-4 h-4" style={{ color: '#9E6752' }} />
                        Create a Questionnaire
                      </p>
                      <p className="text-sm leading-snug" style={{ color: darkMode?.isDark ? 'rgba(254, 215, 165, 0.7)' : '#73766A' }}>
                        Send {recipient.name} a few quick questions about their style and wishes. Their answers help you find gifts they'll actually love.
                      </p>
                    </div>
                    <Button
                      onClick={onCreateQuestionnaire}
                      size="sm"
                      style={{ background: '#9E6752', color: 'white' }}
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Create
                    </Button>
                  </div>
                </div>
              ) : (() => {
                const isAnalyzed = questionnaire.status === 'completed';
                const responseCount = questionnaire.response_count || 0;
                const statusLabel = isAnalyzed
                  ? 'Analysis complete'
                  : responseCount === 0
                    ? 'Awaiting responses'
                    : `${responseCount} response${responseCount === 1 ? '' : 's'} collected`;
                const statusHint = isAnalyzed
                  ? 'Tap View Results to see gift recommendations.'
                  : responseCount === 0
                    ? 'Share the link to start collecting answers.'
                    : 'Ready when you are. Tap Analyze to generate gift suggestions.';
                return (
                <div className="mb-4 p-4 rounded-xl" style={{ background: darkMode?.isDark ? 'rgba(158, 103, 82, 0.2)' : 'rgba(158, 103, 82, 0.1)', border: '1px solid #9E6752' }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Sparkles className="w-5 h-5 flex-shrink-0" style={{ color: '#9E6752' }} />
                      <div className="min-w-0">
                        <p className="font-medium" style={{ color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>
                          {statusLabel}
                        </p>
                        <p className="text-xs" style={{ color: darkMode?.isDark ? 'rgba(254, 215, 165, 0.7)' : '#73766A' }}>
                          {statusHint}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {isAnalyzed ? (
                        <Button
                          onClick={() => window.location.href = `/keepsakes/${questionnaire.id}/results`}
                          size="sm"
                          variant="outline"
                          style={{ borderColor: '#9E6752', color: '#9E6752' }}
                          data-testid="view-results-btn"
                        >
                          View Results
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            if (responseCount > 0 && onAnalyzeQuestionnaire) {
                              onAnalyzeQuestionnaire(questionnaire.id);
                            } else {
                              window.location.href = `/keepsakes/${questionnaire.id}/questionnaire`;
                            }
                          }}
                          disabled={analyzingId === questionnaire.id}
                          size="sm"
                          style={{ background: '#9E6752', color: 'white' }}
                          data-testid="manage-questionnaire-btn"
                        >
                          {analyzingId === questionnaire.id
                            ? 'Analyzing…'
                            : responseCount > 0 ? 'Analyze' : 'Manage'}
                        </Button>
                      )}
                      <Button
                        onClick={() => onDeleteQuestionnaire && onDeleteQuestionnaire(questionnaire.id)}
                        size="sm"
                        variant="outline"
                        style={{ borderColor: '#EF4444', color: '#EF4444' }}
                        data-testid="delete-questionnaire-btn"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })()}
              
              {/* Show ALL interests + notes, appears at the top of the
                  profile content so context is visible at a glance. */}
              {((recipient.interests && recipient.interests.length > 0) || (recipient.notes && recipient.notes.trim())) && (
                <div className="mb-4 space-y-3">
                  {recipient.interests && recipient.interests.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {recipient.interests.map(interest => (
                        <span
                          key={interest}
                          className="text-xs px-2 py-1 rounded-full font-medium"
                          style={{
                            background: darkMode?.isDark ? `${cardColor}40` : `${cardColor}20`,
                            color: darkMode?.isDark ? '#FFFAF5' : '#534145',
                            border: darkMode?.isDark ? `1px solid ${cardColor}60` : 'none'
                          }}
                          data-testid={`recipient-interest-pill-${interest}`}
                        >
                          {interest}
                        </span>
                      ))}
                    </div>
                  )}
                  {recipient.notes && recipient.notes.trim() && (
                    <div
                      className="p-3 rounded-lg text-sm leading-relaxed"
                      style={{
                        background: darkMode?.isDark ? 'rgba(255,255,255,0.06)' : '#FAFAFA',
                        border: `1px solid ${darkMode?.isDark ? 'rgba(255,255,255,0.08)' : '#EDEDED'}`,
                        color: darkMode?.isDark ? 'rgba(255,250,245,0.85)' : '#534145',
                        whiteSpace: 'pre-wrap'
                      }}
                      data-testid="recipient-notes-display"
                    >
                      <span className="text-xs uppercase tracking-wide font-semibold opacity-60 mr-1.5">Notes</span>
                      {recipient.notes}
                    </div>
                  )}
                </div>
              )}
              
              {/* Gifts Section */}
              {savedGifts.length === 0 ? (
                // ONBOARDING EMPTY STATE - Guided steps
                <div className="rounded-xl p-6" style={{ background: darkMode?.isDark ? 'rgba(255,255,255,0.04)' : '#fafafa' }}>
                  <div className="text-center mb-6">
                    <Package className="w-12 h-12 mx-auto mb-3" style={{ color: cardColor, opacity: 0.6 }} />
                    <h4 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Playfair Display, serif', color: darkMode?.isDark ? '#FFFAF5' : '#20212B' }}>
                      Get started with {recipient.name}'s gifts
                    </h4>
                    <p className="text-sm" style={{ color: darkMode?.isDark ? 'rgba(255,250,245,0.6)' : '#73766A' }}>
                      Complete these steps to find the perfect gifts
                    </p>
                  </div>

                  {/* Inline Quick Add (compact in-card search), kept right
                      under the heading so it's the first action available */}
                  <div className="mb-3">
                    <InlineGiftAdder
                      recipientId={recipient.id}
                      onAdded={onInlineGiftAdded}
                      darkMode={darkMode}
                    />
                  </div>
                  
                  {/* Step 1: Add an Occasion */}
                  <div 
                    className="flex items-center gap-4 p-4 rounded-xl mb-3 cursor-pointer transition-all hover:scale-[1.01]"
                    style={{ 
                      background: hasOccasions 
                        ? (darkMode?.isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)')
                        : (darkMode?.isDark ? 'rgba(255,255,255,0.1)' : 'white'),
                      border: `1px solid ${hasOccasions ? 'rgba(16, 185, 129, 0.3)' : (darkMode?.isDark ? 'rgba(255,255,255,0.1)' : '#e5e5e5')}`
                    }}
                    onClick={(e) => { e.stopPropagation(); onManageOccasions(); }}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: hasOccasions ? 'rgba(16, 185, 129, 0.2)' : `${cardColor}20` }}
                    >
                      <Calendar className="w-5 h-5" style={{ color: hasOccasions ? '#10B981' : cardColor }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm" style={{ color: darkMode?.isDark ? '#FFFAF5' : '#20212B' }}>
                        {hasOccasions ? `${recipient.occasions.length} occasion${recipient.occasions.length > 1 ? 's' : ''} added` : 'Add an occasion'}
                      </p>
                      <p className="text-xs" style={{ color: darkMode?.isDark ? 'rgba(255,250,245,0.6)' : '#73766A' }}>
                        {hasOccasions ? 'We\'ll remind you when it\'s coming up' : 'Birthday, anniversary, or any special day'}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: darkMode?.isDark ? 'rgba(255,250,245,0.4)' : '#9ca3af' }} />
                  </div>
                  
                  {/* Step 2: Tell us about them OR Send questionnaire */}
                  <div 
                    className="flex items-center gap-4 p-4 rounded-xl mb-3 cursor-pointer transition-all hover:scale-[1.01]"
                    style={{ 
                      background: hasPreferences || hasQuestionnaire
                        ? (darkMode?.isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)')
                        : (darkMode?.isDark ? 'rgba(255,255,255,0.1)' : 'white'),
                      border: `1px solid ${hasPreferences || hasQuestionnaire ? 'rgba(16, 185, 129, 0.3)' : (darkMode?.isDark ? 'rgba(255,255,255,0.1)' : '#e5e5e5')}`
                    }}
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      if (hasQuestionnaire) {
                        // View questionnaire results/responses
                        if (questionnaire.status === 'completed') {
                          window.location.href = `/keepsakes/${questionnaire.id}/results`;
                        } else if (questionnaire.response_count > 0) {
                          window.location.href = `/keepsakes/${questionnaire.id}/responses`;
                        }
                      } else {
                        // Preferences now live inside Edit Profile
                        onEditRecipient();
                      }
                    }}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: hasPreferences || hasQuestionnaire ? 'rgba(16, 185, 129, 0.2)' : `${cardColor}20` }}
                    >
                      <Gift className="w-5 h-5" style={{ color: hasPreferences || hasQuestionnaire ? '#10B981' : cardColor }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm" style={{ color: darkMode?.isDark ? '#FFFAF5' : '#20212B' }}>
                        {hasPreferences ? 'Preferences saved' : hasQuestionnaire ? 'Questionnaire sent' : 'Tell us about them'}
                      </p>
                      <p className="text-xs" style={{ color: darkMode?.isDark ? 'rgba(255,250,245,0.6)' : '#73766A' }}>
                        {hasPreferences 
                          ? `${recipient.gift_preferences.personality || 'Personality'} · ${recipient.gift_preferences.interests?.slice(0, 2).join(', ') || 'Interests set'}`
                          : hasQuestionnaire 
                            ? `${questionnaire.response_count || 0} responses received`
                            : 'Their personality, interests, and budget'}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: darkMode?.isDark ? 'rgba(255,250,245,0.4)' : '#9ca3af' }} />
                  </div>
                  
                  {/* Alternative: Send a questionnaire */}
                  {!hasQuestionnaire && !hasPreferences && (
                    <div 
                      className="flex items-center gap-4 p-4 rounded-xl mb-3 cursor-pointer transition-all hover:scale-[1.01]"
                      style={{ 
                        background: darkMode?.isDark ? 'rgba(255,255,255,0.1)' : 'white',
                        border: `1px solid ${darkMode?.isDark ? 'rgba(255,255,255,0.1)' : '#e5e5e5'}`
                      }}
                      onClick={(e) => { e.stopPropagation(); onCreateQuestionnaire(); }}
                    >
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: `${cardColor}20` }}
                      >
                        <Users className="w-5 h-5" style={{ color: cardColor }} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm" style={{ color: darkMode?.isDark ? '#FFFAF5' : '#20212B' }}>
                          Or send a questionnaire
                        </p>
                        <p className="text-xs" style={{ color: darkMode?.isDark ? 'rgba(255,250,245,0.6)' : '#73766A' }}>
                          Let their friends & family help you pick
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: darkMode?.isDark ? 'rgba(255,250,245,0.4)' : '#9ca3af' }} />
                    </div>
                  )}
                  
                  {/* Main CTA: Find Gifts */}
                  <Button 
                    onClick={handleFindGifts}
                    className="w-full mt-4 transition-all hover:scale-[1.02]"
                    style={{ 
                      background: `linear-gradient(135deg, ${cardColor} 0%, ${cardColor}dd 100%)`, 
                      color: 'white', 
                      borderRadius: '12px',
                      padding: '14px',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    <Search className="w-4 h-4 mr-2" />
                    {hasAnyProfileData ? 'Find Perfect Gifts' : 'Browse All Gifts'}
                  </Button>
                </div>
              ) : (
                // HAS GIFTS - Show grid with single Find Gifts button
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <p className="text-sm font-medium" style={{ color: darkMode?.isDark ? '#FFFAF5' : '#534145' }}>
                      {giftCount} saved {giftCount === 1 ? 'gift' : 'gifts'}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <InlineGiftAdder
                        recipientId={recipient.id}
                        onAdded={onInlineGiftAdded}
                        darkMode={darkMode}
                      />
                      <Button 
                        onClick={handleFindGifts}
                        size="sm"
                        style={{ 
                          background: cardColor, 
                          color: 'white', 
                          borderRadius: '20px'
                        }}
                      >
                        <Search className="w-3.5 h-3.5 mr-1.5" />
                        {hasAnyProfileData ? 'Find More' : 'Browse'}
                      </Button>
                    </div>
                  </div>
                  {(() => {
                    // Group saved gifts by deck_id (bundled decks) vs ungrouped (standalone)
                    const decks = new Map(); // deck_id -> {deck_name, items[]}
                    const ungrouped = [];
                    savedGifts.forEach(item => {
                      const deckId = item.saved_gift?.deck_id;
                      if (deckId) {
                        if (!decks.has(deckId)) {
                          decks.set(deckId, { deck_name: item.saved_gift.deck_name || 'Bundled Deck', items: [] });
                        }
                        decks.get(deckId).items.push(item);
                      } else {
                        ungrouped.push(item);
                      }
                    });
                    return (
                      <>
                        {Array.from(decks.entries()).map(([deckId, group]) => (
                          <div 
                            key={`deck-bundle-${deckId}`}
                            data-testid={`bundled-deck-${deckId}`}
                            className="mb-6 p-4 rounded-xl"
                            style={{
                              background: darkMode?.isDark ? 'rgba(255,255,255,0.04)' : `${cardColor}08`,
                              border: `2px dashed ${darkMode?.isDark ? `${cardColor}99` : `${cardColor}50`}`
                            }}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <Package className="w-4 h-4" style={{ color: cardColor }} />
                              <p className="text-sm font-semibold" style={{ color: darkMode?.isDark ? '#FFFAF5' : '#20212B' }}>
                                {group.deck_name}
                              </p>
                              <span 
                                className="text-xs px-2 py-0.5 rounded-full"
                                style={{ background: `${cardColor}30`, color: darkMode?.isDark ? '#FFFAF5' : '#20212B' }}
                              >
                                {group.items.length} items · Deck
                              </span>
                            </div>
                            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-5 sm:gap-x-4 sm:gap-y-6">
                              {group.items.map(({ saved_gift, product }) => (
                                <div key={saved_gift.id} className="relative group">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); onRemoveGift(saved_gift.id); }}
                                    className="absolute top-2 right-2 z-10 p-2 rounded-full transition-all hover:scale-110 opacity-0 group-hover:opacity-100"
                                    style={{
                                      background: 'rgba(0, 0, 0, 0.6)',
                                      backdropFilter: 'blur(4px)'
                                    }}
                                  >
                                    <X className="w-4 h-4" style={{ color: 'white' }} />
                                  </button>
                                  {saved_gift.source === 'questionnaire' && (
                                    <span
                                      className="absolute top-2 left-2 z-10 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium leading-none"
                                      style={{
                                        background: 'rgba(158, 103, 82, 0.92)',
                                        color: 'white',
                                        backdropFilter: 'blur(4px)'
                                      }}
                                      title="Added from questionnaire analysis"
                                      data-testid={`gift-from-questionnaire-${saved_gift.id}`}
                                    >
                                      <Sparkles className="w-3 h-3" />
                                      From questionnaire
                                    </span>
                                  )}
                                  <ProductCard
                                    product={product}
                                    user={user}
                                    hideHeartButton={true}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        {ungrouped.length > 0 && (
                          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-5 sm:gap-x-4 sm:gap-y-6">
                            {ungrouped.map(({ saved_gift, product }) => (
                              <div key={saved_gift.id} className="relative group">
                                <button
                                  onClick={(e) => { e.stopPropagation(); onRemoveGift(saved_gift.id); }}
                                  className="absolute top-2 right-2 z-10 p-2 rounded-full transition-all hover:scale-110 opacity-0 group-hover:opacity-100"
                                  style={{
                                    background: 'rgba(0, 0, 0, 0.6)',
                                    backdropFilter: 'blur(4px)'
                                  }}
                                >
                                  <X className="w-4 h-4" style={{ color: 'white' }} />
                                </button>
                                {saved_gift.source === 'questionnaire' && (
                                  <span
                                    className="absolute top-2 left-2 z-10 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium leading-none"
                                    style={{
                                      background: 'rgba(158, 103, 82, 0.92)',
                                      color: 'white',
                                      backdropFilter: 'blur(4px)'
                                    }}
                                    title="Added from questionnaire analysis"
                                    data-testid={`gift-from-questionnaire-${saved_gift.id}`}
                                  >
                                    <Sparkles className="w-3 h-3" />
                                    From questionnaire
                                  </span>
                                )}
                                <ProductCard
                                  product={product}
                                  user={user}
                                  hideHeartButton={true}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-md" style={{ backgroundColor: darkMode?.isDark ? '#2D4354' : 'white' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif', color: darkMode?.isDark ? '#FED7A5' : '#20212B' }}>
              Share {recipient.name}'s Profile
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm" style={{ color: darkMode?.isDark ? 'rgba(254, 215, 165, 0.7)' : '#73766A' }}>
              Anyone with this link can view {recipient.name}'s profile, interests, occasions, and saved gifts.
            </p>
            
            <div className="flex gap-2">
              <input
                type="text"
                value={shareLink}
                readOnly
                className="flex-1 px-3 py-2 rounded-lg border text-sm"
                style={{
                  backgroundColor: darkMode?.isDark ? 'rgba(255, 255, 255, 0.1)' : '#F5F5F5',
                  color: darkMode?.isDark ? '#FED7A5' : '#20212B',
                  border: `1px solid ${darkMode?.isDark ? 'rgba(254, 215, 165, 0.3)' : '#E5E5E5'}`
                }}
              />
              <Button
                onClick={copyLink}
                style={{ background: copied ? '#10B981' : '#9E6752', color: 'white' }}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

